/**
 * VHS tape pass (DECISIONS.md AMD-3).
 *
 * Four artefacts, all of them bounded:
 *
 *  - **Tape wobble** — a slow low-frequency horizontal displacement that varies down the
 *    frame, the head drum not tracking perfectly.
 *  - **Timebase error** — a much slower whole-field drift on top of the wobble.
 *  - **Head-switching band** — the noise stripe a VHS deck leaves in the bottom few
 *    percent of the picture where the heads change over.
 *  - **Chroma bleed** — luma and chroma were recorded in different bands, so colour
 *    smears sideways relative to brightness.
 *
 * AMD-3's readability guard is the binding constraint: **no sample is displaced by more
 * than one internal pixel**, so a telegraph edge (§11.9, 0.10 u stroke at full alpha)
 * can never be pushed off the thing it belongs to. Every displacement term below is
 * expressed in texels and clamped by `uMaxShift`, which the pass pins to 1.0. The
 * head-switch band therefore carries its energy as noise and desaturation rather than as
 * the large shear a real deck produces.
 *
 * Runs before bloom so the wobble carries into the glow instead of sliding underneath
 * it, and it is fully disableable (`uIntensity` 0 skips the pass entirely).
 */

import { GLSL_COLOR } from './common';

export const VHS_FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tSource;
uniform vec2 uTexel;
uniform vec2 uResolution;
uniform float uTime;
uniform float uIntensity;
/** Hard cap on any displacement, in source texels. Pinned to 1.0 by the pass. */
uniform float uMaxShift;

in vec2 vUv;
out vec4 fragColor;

${GLSL_COLOR}

void main() {
  float y = vUv.y * uResolution.y;

  // Tape wobble: two incommensurate frequencies down the frame so the pattern never
  // settles into a visible repeat, plus a slow whole-field timebase drift.
  float wobble =
      sin(y * 0.21 + uTime * 5.7) * 0.55
    + sin(y * 0.043 - uTime * 1.9) * 0.45;
  float timebase = sin(uTime * 0.83) * 0.35 + sin(uTime * 0.17) * 0.2;

  // Head-switching: the bottom 4% of the field, gated so it only fires periodically.
  float bandPos = smoothstep(0.055, 0.0, vUv.y);
  float switching = bandPos * (0.5 + 0.5 * sin(uTime * 3.1));

  float shift = (wobble + timebase + switching * 1.4) * uIntensity;
  shift = clamp(shift, -uMaxShift, uMaxShift) * uTexel.x;

  // Chroma bleed: colour lags luma sideways. Same cap, opposite sign per channel.
  float chroma = clamp(0.6 * uIntensity, 0.0, uMaxShift) * uTexel.x;

  vec2 uv = vec2(vUv.x + shift, vUv.y);
  vec3 c;
  c.r = texture(tSource, vec2(uv.x + chroma, uv.y)).r;
  c.g = texture(tSource, uv).g;
  c.b = texture(tSource, vec2(uv.x - chroma, uv.y)).b;

  // Dropout speckle inside the switching band only, and it lifts luminance rather than
  // punching holes: a black dropout over a hazard fill would read as a gap in the fill.
  float n = hash12(vec2(floor(vUv.x * uResolution.x), floor(y) + floor(uTime * 60.0)));
  c += vec3(n * switching * uIntensity * 0.22);

  // Luma-only tape noise, well under the §11.8 flash luminance budget.
  c *= 1.0 + (n - 0.5) * uIntensity * 0.05;

  fragColor = vec4(max(c, vec3(0.0)), 1.0);
}
`;
